import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { useScript } from "@Hooks/useScript";
import cheerzLogo from "@Images/logo-cheerz.png";
import { joinUsLink, newRequestLink } from "@Utils/links";
import "./style.scss";

const Contact: FunctionComponent = () => {
  const { isMediumScreen, isSmallScreen } = useBreakpoint();
  const intl = useIntl();
  const googleApiUrl = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    ? `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    : undefined;
  const [error, loaded] = useScript(googleApiUrl);
  const [displayOverlayTiles, setDisplayOverlayTiles] = useState<string[]>([]);

  const tertiaryGreen37Color = "#2c9358";
  const primary40Color = "#0053cc";
  const tertiaryDarkpink62Color = "#fa4364";
  const tertiaryYellow50Color = "#ffc100";
  const tiles = [
    {
      link: { translationId: "cheerz_Footer_links_help_contact", url: newRequestLink[intl.locale] },
      name: "customerSupport",
      overlayBackgroundColor: tertiaryGreen37Color,
    },
    {
      link: { translationId: "cheerz_Contact_partnerships_link", url: newRequestLink[intl.locale] },
      name: "partnerships",
      overlayBackgroundColor: primary40Color,
    },
    {
      link: { translationId: "cheerz_Contact_jobs_link", url: joinUsLink },
      name: "jobs",
      overlayBackgroundColor: tertiaryDarkpink62Color,
    },
    {
      link: {
        translationId: "cheerz_Contact_press_link",
        url: `mailto:${intl.formatMessage({ id: "cheerz_Contact_press_link" })}
      `,
      },
      name: "press",
      overlayBackgroundColor: tertiaryYellow50Color,
    },
  ];

  const cheerzAddress = (
    <>
      <img alt="Cheerz" className="Contact-place-logo" src={cheerzLogo} />
      <p className="Contact-place-address">
        7 rue de Bucarest <br />4<sup>e</sup>, 5<sup>e</sup> et 6<sup>e</sup> étages <br />
        75008 Paris <br />
        France
      </p>
    </>
  );

  const handleClick = (tileName: string) =>
    new Set(displayOverlayTiles).has(tileName)
      ? setDisplayOverlayTiles((oldArray) => oldArray.filter((value) => value !== tileName))
      : setDisplayOverlayTiles((oldArray) => oldArray.concat(tileName));

  useEffect(() => {
    if (!isMediumScreen && loaded && !error) {
      const cheerzLocation = { lat: 48.88064, lng: 2.32631 };
      const map = new google.maps.Map(document.getElementById("map") as HTMLElement, {
        center: cheerzLocation,
        zoom: 15,
      });
      new google.maps.Marker({
        map: map,
        position: cheerzLocation,
      });
    }
  }, [error, loaded]);

  return (
    <div className="Contact">
      <div className="Contact-image">
        <h1 className="Contact-title">{intl.formatMessage({ id: "cheerz_Contact_title" })}</h1>
        <p className="Contact-subtitle">{intl.formatMessage({ id: "cheerz_Contact_subtitle" })}</p>
      </div>
      <div className="Contact-tiles">
        {tiles.map((tile) => {
          return (
            <div className="Contact-tile" key={tile.name}>
              <h2 className="Contact-tile-title">
                {intl.formatMessage({ id: `cheerz_Contact_${tile.name}_title` })}
              </h2>
              <p className="Contact-tile-subtitle">
                {intl.formatMessage({ id: `cheerz_Contact_${tile.name}_subtitle` })}
              </p>
              <ButtonSmily onClick={() => handleClick(tile.name)} size="s" type="primary">
                {intl.formatMessage({ id: "cheerz_Footer_links_help_contact" })}
              </ButtonSmily>
              <div
                className="Contact-tile-overlay"
                style={{
                  backgroundColor: tile.overlayBackgroundColor,
                  transform: new Set(displayOverlayTiles).has(tile.name) ? "translateY(0)" : "",
                }}
              >
                <div
                  className="Contact-tile-overlay-title"
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}
                >
                  {intl.formatMessage({ id: `cheerz_Contact_${tile.name}_title` })}
                </div>
                <div className="Contact-tile-overlay-links">
                  <a className="Contact-tile-overlay-link" href={tile.link.url}>
                    {intl.formatMessage({ id: tile.link.translationId })}
                  </a>
                </div>
                <ButtonSmily onClick={() => handleClick(tile.name)} size="s" type="tertiary">
                  {intl.formatMessage({ id: "cheerz_Drafts_cancel_button" })}
                </ButtonSmily>
              </div>
            </div>
          );
        })}
      </div>
      {!isSmallScreen && loaded && !error ? (
        <div className="Contact-location">
          <div className="Contact-location-map" id="map" />
          <div className="Contact-location-place">{cheerzAddress}</div>
        </div>
      ) : (
        <div className="Contact-place">{cheerzAddress}</div>
      )}
    </div>
  );
};

export default Contact;
